import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { Star, Quote } from 'lucide-react';
import { opinionsApi, Opinion } from '@/services/apiService';

const TestimonialsSection = () => {
    const { language, t } = useLanguage();
    const [opinions, setOpinions] = useState<Opinion[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOpinions = async () => {
            try {
                const data = await opinionsApi.getAll();
                setOpinions(data);
            } catch (error) {
                console.error("Failed to load opinions:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchOpinions();
    }, []);

    const staticTestimonials = {
        PL: [
            {
                name: 'Anna K.',
                date: 'Luty 2026',
                rating: 5,
                text: 'Zamówiłam zestaw prezentowy na rocznicę ślubu rodziców — byli zachwyceni! Wino pięknie pachniało, a opakowanie było eleganckie. Na pewno wrócę po więcej.',
            },
            {
                name: 'Marek W.',
                date: 'Styczeń 2026',
                rating: 5,
                text: 'Najlepsze Primitivo jakie piłem w Polsce. Czuć, że to prawdziwe włoskie wino, nie masowa produkcja. Szybka dostawa i starannie zapakowane.',
            },
            {
                name: 'Katarzyna D.',
                date: 'Grudzień 2025',
                rating: 5,
                text: 'Oliwa extra vergine jest fantastyczna — taka jak we Włoszech! Zamawiam regularnie i nigdy się nie zawiodłam. Polecam serdecznie każdemu.',
            },
            {
                name: 'Tomasz B.',
                date: 'Marzec 2026',
                rating: 5,
                text: 'Znakomita obsługa i piękne produkty. Chianti które zamówiłem idealnie pasowało do kolacji z przyjaciółmi. Autentyczny smak Toskanii!',
            },
        ],
        EN: [
            {
                name: 'Anna K.',
                date: 'February 2026',
                rating: 5,
                text: 'I ordered a gift set for my parents\' wedding anniversary — they were delighted! The wine smelled beautiful and the packaging was elegant. I\'ll definitely be back for more.',
            },
            {
                name: 'Marek W.',
                date: 'January 2026',
                rating: 5,
                text: 'The best Primitivo I\'ve had in Poland. You can tell it\'s real Italian wine, not mass production. Fast delivery and carefully packaged.',
            },
            {
                name: 'Katarzyna D.',
                date: 'December 2025',
                rating: 5,
                text: 'The extra virgin olive oil is fantastic — just like in Italy! I order regularly and have never been disappointed. Highly recommend to everyone.',
            },
            {
                name: 'Tomasz B.',
                date: 'March 2026',
                rating: 5,
                text: 'Excellent service and beautiful products. The Chianti I ordered was perfect for dinner with friends. Authentic taste of Tuscany!',
            },
        ],
    };

    const reviews = opinions.length > 0
        ? opinions.map(op => ({
            name: op.name,
            date: op.date,
            rating: op.rating,
            text: op.text
        }))
        : staticTestimonials[language];

    const renderStars = (rating: number) => {
        return (
            <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => {
                    const isFull = rating >= star;
                    const isHalf = rating >= star - 0.5 && rating < star;
                    return (
                        <div key={star} className="relative">
                            <Star className={`h-4 w-4 text-slate-300 dark:text-slate-700`} />
                            {isFull && (
                                <Star className="absolute top-0 left-0 h-4 w-4 fill-amber-400 text-amber-400" />
                            )}
                            {isHalf && (
                                <div className="absolute top-0 left-0 overflow-hidden w-1/2">
                                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const averageRating = opinions.length > 0
        ? (opinions.reduce((sum, op) => sum + op.rating, 0) / opinions.length).toFixed(1)
        : "4.9";

    if (loading) {
        return (
            <section className="bg-background py-20 overflow-hidden">
                <div className="container mx-auto px-4 flex justify-center items-center h-64">
                    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
            </section>
        );
    }

    return (
        <section className="relative overflow-hidden bg-[#FAF7F2] pt-28 pb-20 md:pt-36 md:pb-28">
            {/* Elegant wave transition from previous white section */}
            <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0] z-10 pointer-events-none">
                <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-12 md:h-20 text-[#FAF7F2] fill-current transform rotate-180">
                    <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V120H0C56.63,120,116.89,114,171.7,92.83,224.45,72.47,270,66.11,321.39,56.44Z" className="fill-white"></path>
                </svg>
            </div>

            {/* ===== Animated background decorations ===== */}

            {/* Slowly drifting blobs */}
            <motion.div
                className="absolute top-10 left-[10%] h-72 w-72 rounded-full bg-primary/[0.04] blur-[80px] pointer-events-none"
                animate={{ x: [0, 40, -20, 0], y: [0, -30, 20, 0] }}
                transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                className="absolute bottom-10 right-[10%] h-80 w-80 rounded-full bg-accent/[0.05] blur-[90px] pointer-events-none"
                animate={{ x: [0, -30, 25, 0], y: [0, 25, -15, 0] }}
                transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-96 w-96 rounded-full bg-primary/[0.02] blur-[100px] pointer-events-none"
                animate={{ scale: [1, 1.15, 0.95, 1] }}
                transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Floating small decorative circles */}
            {[
                { size: 6, left: '15%', top: '20%', delay: 0, dur: 12 },
                { size: 4, left: '80%', top: '15%', delay: 2, dur: 15 },
                { size: 8, left: '70%', top: '75%', delay: 4, dur: 18 },
                { size: 5, left: '25%', top: '80%', delay: 1, dur: 14 },
                { size: 3, left: '50%', top: '10%', delay: 3, dur: 16 },
                { size: 7, left: '90%', top: '50%', delay: 5, dur: 20 },
                { size: 4, left: '5%', top: '55%', delay: 2, dur: 13 },
            ].map((dot, i) => (
                <motion.div
                    key={i}
                    className="absolute rounded-full bg-primary/[0.07] pointer-events-none"
                    style={{
                        width: dot.size,
                        height: dot.size,
                        left: dot.left,
                        top: dot.top,
                    }}
                    animate={{
                        y: [0, -20, 10, -15, 0],
                        x: [0, 10, -8, 5, 0],
                        opacity: [0.4, 0.8, 0.5, 0.9, 0.4],
                    }}
                    transition={{
                        duration: dot.dur,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: dot.delay,
                    }}
                />
            ))}

            {/* Subtle animated ring outlines */}
            <motion.div
                className="absolute top-[15%] right-[20%] h-32 w-32 rounded-full border border-primary/[0.06] pointer-events-none"
                animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3], rotate: [0, 180, 360] }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            />
            <motion.div
                className="absolute bottom-[20%] left-[15%] h-24 w-24 rounded-full border border-accent/[0.06] pointer-events-none"
                animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.5, 0.2], rotate: [360, 180, 0] }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            />

            {/* ===== Content ===== */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "LocalBusiness",
                    "name": "Włoska Piwnica",
                    "aggregateRating": {
                        "@type": "AggregateRating",
                        "ratingValue": averageRating,
                        "reviewCount": opinions.length > 0 ? opinions.length : staticTestimonials[language as keyof typeof staticTestimonials].length
                    },
                    "review": reviews.map(r => ({
                        "@type": "Review",
                        "reviewRating": {
                            "@type": "Rating",
                            "ratingValue": r.rating
                        },
                        "author": {
                            "@type": "Person",
                            "name": r.name
                        },
                        "reviewBody": r.text
                    }))
                })}
            </script>
            <div className="container relative z-10 mx-auto px-4">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mb-14 text-center"
                >
                    <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1 text-sm font-medium text-primary">
                        <Star className="h-4 w-4 fill-primary" />
                        {t('testimonialsLabel')}
                    </span>
                    <h2 className="mb-4 font-display text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
                        {t('testimonialsTitle')}
                    </h2>
                    <p className="mx-auto max-w-2xl text-muted-foreground">
                        {t('testimonialsSubtitle')}
                    </p>
                    <div className="mx-auto mt-4 h-1 w-24 rounded-full bg-primary" />
                </motion.div>

                {/* Reviews Grid */}
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                    {reviews.map((review, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1, duration: 0.5 }}
                            className="group relative flex flex-col rounded-[24px] border border-[#E5DEC9] bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-primary/5 hover:border-primary/20"
                        >
                            {/* Quote icon */}
                            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                                <Quote className="h-5 w-5" />
                            </div>

                            {/* Stars */}
                            <div className="mb-3">
                                {renderStars(review.rating)}
                            </div>

                            {/* Text */}
                            <p className="mb-4 flex-1 text-sm leading-relaxed text-muted-foreground italic">
                                "{review.text}"
                            </p>

                            {/* Author */}
                            <div className="mt-auto border-t border-border/50 pt-4">
                                <p className="font-semibold text-foreground text-sm">
                                    {review.name}
                                </p>
                                <p className="text-xs text-muted-foreground">{review.date}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Overall rating */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-10 flex flex-col items-center justify-center gap-2 text-center"
                >
                    <div className="mb-1">
                        {renderStars(parseFloat(averageRating))}
                    </div>
                    <p className="text-sm text-muted-foreground">
                        <span className="font-bold text-foreground">{averageRating}/5</span> — {t('testimonialsBasedOn')}
                    </p>
                </motion.div>
            </div>
        </section>
    );
};

export default TestimonialsSection;
